import { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import { Spin } from "antd";
import axios from "axios";
import * as Sentry from "@sentry/react";
import "./style.css";

// Global API timeout — safety net for truly hung requests only.
// Per-context timeouts are set in sync/payment flows (60s/120s).
// This catches anything that slips through without a specific timeout.
axios.defaults.timeout = 60000;

// UI Watchdog — detects frozen UI and auto-reloads after 15s
import { startWatchdog } from "../utils/watchdog";
// Health Reporter — collects metrics, computes health score, reports every 5min
import { startHealthReporter } from "../utils/healthReporter";
startWatchdog();
startHealthReporter();
import ErrorBoundary from "./ErrorBoundary";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { EventBusProvider } from "../lib/components/EventBusProvider";
import OrderNotification from "./OrderNotification";
import SignIn from "./Authentication";
import tenantConfig from "../config/tenantConfig";
import { initTenantStorage } from "../config/tenantStorage";
import useAuthStore from "../stores/useAuthStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useCartStore from "../stores/useCartStore";
import useSessionStore from "../stores/useSessionStore";
import useTransactionStore from "../stores/useTransactionStore";
import useRestaurantStore from "../stores/useRestaurantStore";
import useReferenceStore from "../stores/useReferenceStore";
import useAdvanceOrderStore from "../stores/useAdvanceOrderStore";
import PrivateRoute from "./PrivateRoute";
import NavigationBlocker from "../NavigationBlocker";

import TillModalHandler from "./TillModalHandler";
import SupportButton from "./Support/SupportButton";
import CustomerReviewHost from "./CustomerReview/CustomerReviewHost";
import { PrintFailureBar } from "../lib/printer/printFailureNotice";
import CrashRecoveryProvider from "./CrashRecoveryProvider";
import SingleInstanceGuard from "./SingleInstanceGuard";
import { installPrintInterceptor } from "../lib/printer/printInterceptor";
import { installPrintServiceErrorNotifier } from "../lib/printer/printTemplateErrors";
import { checkAppVersionGate, checkSchemaVersionGate, stampCurrentVersion } from "../utils/appVersionGate";
import { trace } from "../utils/debugTrace";
import { clearB2bFlagForDefaultCustomer } from "../utils/b2bEntryState";
import { pickDefaultRoute, parseTillAccess, sessionOwnerMatchesCurrentUser } from "../constants/accessControl";
import { getOAuthHeaders } from "../constants/oAuthValidation";

// POS routes
import PointOfSale from "./PointOfSale";
import DualScreenPOS from "./dualScreen";
import Till from "./Till/Till";
import Login from "./Login";
import Sync from "./Sync";
import OpenTill from "./OpenTill";
import CloseTill from "./CloseTill";
import CloseTillAll from "./CloseTill/CloseTIllAll";
import CashToKeep from "./CloseTill/cashToKeep";
import OpenTillComponent from "./OpenTill/OpenTillComponent";
import Dashboard2 from "./Dashboard2";
import ProductionOrder from "./ProductionOrder";
import ProductionRMIssue from "./ProductionRMIssue";
import PurchaseOrder from "./PurchaseOrder";
import Inventory from "./Inventory";
import Aggregator from "./Aggregator";
import CallCenter from "./CallCenter";
import Estimate from "./Estimate";
import Preorder from "./PreOrder";
import RestaurantPreorder from "./PointOfSale/Restaurant/restaurantPreorder";
import TableManagement from "./PointOfSale/Restaurant/Tables-Management/tables";
import HealthMonitor from "./HealthMonitor";
import { WebSocketProvider, useWebSocket } from "../lib/components/WebSocketProvider";
import { getSalesHistoryDetails } from "../utils/salesHistoryUtils";
import { parseTillLayout } from "../utils/onlineOrderGate";

// Auth-aware redirect: the till's own reachable screen if an active session exists,
// /till if authenticated but no session, /sign-in if not authenticated.
// TSK-2997: was hardcoded to /pos, which sent estimate/production-only tills to a
// screen they have no access to.
// BUG-1994: hasActiveSession used to trust tillSession/tillData purely off their
// own contents, with no check on WHO they belong to. A user who closes the app
// without logging out (so tillSession/tillData are never cleared) leaves both
// behind for whoever authenticates next — even a different, unentitled user —
// and this redirect would fast-forward that new user straight into POS,
// bypassing /till's own getTills() entitlement check entirely. Comparing the
// stamped tillDataOwner against the currently authenticated cookie identity
// closes that gap: on a mismatch this falls through to /till, which re-runs
// getTills() for the actual current user and rejects them there if they have
// no access — the same recovery path a legitimate same-till cashier handoff
// already goes through when tillDataOwner isn't set (older sessions) or matches.
function AuthRedirect() {
  const authHeaders = getOAuthHeaders();
  // BUG-2594: "signed in" is the login the POS uses, not any accessToken cookie.
  const hasAuth = !!authHeaders?.access_token;
  const currentIdentity = authHeaders?.username || authHeaders?.email || null;
  const tillSession = useSessionStore.getState().tillSession;
  const tillData = useAuthStore.getState().tillData;
  const tillDataOwner = useAuthStore.getState().tillDataOwner;
  const posConfig = usePOSConfigStore.getState().posConfig;
  const identityMatches = sessionOwnerMatchesCurrentUser(tillDataOwner, currentIdentity);
  const hasActiveSession = tillSession?.tillStatus === "open" && tillData?.tillAccess && posConfig && identityMatches;
  const redirectTo = hasAuth ? (hasActiveSession ? pickDefaultRoute({ posConfig, tillaccess: parseTillAccess(tillData) }) : "/till") : "/sign-in";
  // BUG-2066/2067/2068: this catch-all fires whenever the router lands on a
  // pathname that matched none of App.js's explicit <Route>s — in the lock
  // race, that's the intended /sign-in?username=...&readOnly=true collapsing
  // to bare "/" before this document ever mounted (see BootDiag "raw boot
  // URL" below). Landing here with !hasAuth produces a BARE "/sign-in" with
  // no readOnly=true — the exact combination the CW Browser shell's
  // checkNav (electron/main.js) reads as a genuine logout, tearing down the
  // whole app's session instead of just this POS webview's.
  trace("AuthRedirect", "fired", {
    pathnameAtFire: window.location.pathname,
    searchAtFire: window.location.search,
    hasAuth,
    redirectTo,
  });
  return <Redirect to={redirectTo} />;
}

// BUG-2066/2067/2068 diagnostics: correlate against PrivateRoute's own render
// count/StoreDiag.session — if App re-renders here in the same ~250ms window as
// a StoreDiag.session change, that's the propagation path reaching PrivateRoute
// (a child re-render alone can't cause an ancestor like PrivateRoute to
// re-evaluate; App re-rendering can, since it's PrivateRoute's ancestor via
// <Switch>). Remove once root cause is confirmed.
let appRenderCount = 0;

const App = () => {
  appRenderCount += 1;
  trace("AppRenderDiag", "App component render", { appRenderCount, pathname: window.location.pathname });
  const SentryDSN = process.env.REACT_APP_SENTRY_DSN;
  const SentryEnabled = process.env.REACT_APP_ENABLE_SENTRY;
  const environment = process.env.REACT_APP_Sentry_Environment;
  const [tenantReady, setTenantReady] = useState(false);

  const setTokens = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchKey = searchParams.get("searchKey");
    const nameTill = searchParams.get("nameTill");
    if (searchKey) {
      localStorage.setItem("tiilId", searchKey);
      localStorage.setItem("tillName", nameTill);
    }
  };

  useEffect(() => {
    let userPreferences = JSON.parse(localStorage.getItem("userPreferences"));
    if (!userPreferences?.dateFormat) {
      userPreferences = {
        dateFormat: "DD-MM-YYYY",
        dateTimeFormat: "DD-MM-YYYY HH:mm:ss",
      };
      localStorage.setItem("userPreferences", JSON.stringify(userPreferences));
    }
    setTokens();

    // Install print interceptor for Electron embedded printing
    if (window.electronAPI) {
      installPrintInterceptor();
    }
    installPrintServiceErrorNotifier();

    const initTenant = async () => {
      const bootStart = performance.now();
      // BUG-2066/2067/2068: captures the raw URL this document actually
      // booted with — window.location.pathname alone (already logged by
      // SingleInstanceGuard's own mount trace) doesn't show the query
      // string or referrer, both needed to tell "we were handed a URL that
      // already lost ?username=...&readOnly=true before this document ever
      // mounted" apart from "this document had the right URL and something
      // inside it discarded the query before reading it."
      trace("BootDiag", "raw boot URL", {
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        referrer: document.referrer,
      });
      trace("BootDiag", "initTenant start", { path: window.location.pathname });
      try {
        // Version gate: clear stale localStorage when app version or schema changes
        const versionChanged = checkAppVersionGate();
        const schemaChanged = !versionChanged && checkSchemaVersionGate();
        if (versionChanged || schemaChanged) {
          window.location.reload();
          return;
        }
        stampCurrentVersion();

        // Apply a cached tenant prefix synchronously, before any localStorage
        // reads happen below, instead of waiting on tenantConfig.initialize()'s
        // async window.electronAPI.tenant.getActive() IPC round-trip. tillData
        // (useAuthStore, raw localStorage) is only ever written under the
        // tenant-prefixed key (see tenantStorage.js), but until now the prefix
        // was applied ONLY after that IPC call resolved truthy for THIS load.
        // Any time it was momentarily unready/slow/null on a fast refresh,
        // rehydrateFromStorage() below read the unprefixed key instead and
        // found nothing — tillData came back null even though it was safely
        // on disk, forcing a bogus /till → Login → full processSync detour.
        // posConfig never had this problem: it persists via zustand + a
        // sqliteStorage adapter, not this localStorage prefix mechanism.
        const cachedTenantCode = localStorage.getItem("cw-active-tenant");
        if (cachedTenantCode) {
          initTenantStorage(cachedTenantCode);
        }

        const activeTenant = await tenantConfig.initialize();
        trace("BootDiag", "activeTenant resolved", { hasActiveTenant: !!activeTenant, code: activeTenant?.code || null, cachedTenantCode });
        if (activeTenant) {
          initTenantStorage(activeTenant.code);
        }

        try {
          await useAuthStore.getState().rehydrateFromStorage();
        } catch (e) {
          console.error("[App] Auth rehydrate failed:", e);
        }

        const tillDataAfterRehydrate = useAuthStore.getState().tillData;

        const results = await Promise.allSettled([
          usePOSConfigStore.persist.rehydrate(),
          useCartStore.persist.rehydrate(),
          useSessionStore.persist.rehydrate(),
          useTransactionStore.persist.rehydrate(),
          useRestaurantStore.persist.rehydrate(),
          useReferenceStore.persist.rehydrate(),
          useAdvanceOrderStore.persist.rehydrate(),
        ]);
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
          console.error(
            "[App] Store rehydration failures:",
            failed.map((r) => r.reason)
          );
        }

        // BUG-2601: a B2B flag restored with a default-customer cart was never saved
        // for a customer on this cart (the drawer was reloaded before Save).
        try {
          const { cart: restoredCart, exchangeCart: restoredExchangeCart } = useCartStore.getState();
          const restoredB2bFlag = localStorage.getItem("isB2bEnabled");
          const cleared = clearB2bFlagForDefaultCustomer({
            cart: restoredCart,
            exchangeCart: restoredExchangeCart,
            tillData: useAuthStore.getState().tillData,
            transactionStore: useTransactionStore,
          });
          if (cleared) {
            console.warn("[BUG-2601][startup] B2B flag cleared: the restored cart has the default customer", {
              sOrderID: restoredCart?.sOrderID || null,
              storedFlag: restoredB2bFlag,
            });
            trace("B2B", "startup-flag-cleared-default-customer", { sOrderID: restoredCart?.sOrderID || null, storedFlag: restoredB2bFlag });
          }
        } catch (e) {
          console.error("[BUG-2601][startup] B2B flag check failed:", e);
        }

        // BUG-1564: Restore the POS route after browser refresh.
        // CW Browser resets the URL to / on F5, which hits AuthRedirect → /till.
        // After full rehydration, if the session is valid and we're on a reset
        // path, replace the URL before the router renders — no flicker.
        // TSK-2997: resolve the target from the till's own mode instead of a
        // hardcoded /pos, so an estimate/production till doesn't take an extra
        // /pos → gate → real screen hop on every refresh.
        const rehydratedSession = useSessionStore.getState().tillSession;
        const rehydratedTill = useAuthStore.getState().tillData;
        const rehydratedPosConfig = usePOSConfigStore.getState().posConfig;
        // BUG-2594: "signed in" is the login the POS uses, not any accessToken cookie.
        const hasAuth = !!getOAuthHeaders()?.access_token;
        const path = window.location.pathname;
        // BUG-1994: rehydratedTill/rehydratedSession are whatever was last
        // persisted to disk, regardless of who wrote them — a till is shared
        // hardware and neither survives a plain logout nor gets cleared by an
        // abrupt browser close. Without comparing tillDataOwner against the
        // CURRENT cookie identity (same check AuthRedirect and Till.js's
        // same-day fast-forward already do — see accessControl.js), this
        // block would rewrite the URL straight to /pos using a previous
        // user's stale tillAccess before the router — and therefore
        // ModuleGate — ever gets a chance to validate the new user.
        const restoreIdentity = getOAuthHeaders()?.username || getOAuthHeaders()?.email || null;
        const restoreIdentityMatches = sessionOwnerMatchesCurrentUser(useAuthStore.getState().tillDataOwner, restoreIdentity);
        trace("BootDiag", "rehydration complete", {
          elapsedMs: Math.round(performance.now() - bootStart),
          hasAuth,
          hasTillSession: !!rehydratedSession,
          tillStatus: rehydratedSession?.tillStatus,
          hasTillData: !!rehydratedTill?.tillAccess,
          hasPosConfig: !!rehydratedPosConfig,
          restoreIdentityMatches,
          storeRehydrateFailures: failed.length,
        });
        if (
          hasAuth &&
          restoreIdentityMatches &&
          rehydratedSession?.tillStatus === "open" &&
          rehydratedTill?.tillAccess &&
          rehydratedPosConfig &&
          (path === "/" || path === "/till")
        ) {
          const restoreTo = pickDefaultRoute({
            posConfig: rehydratedPosConfig,
            tillaccess: parseTillAccess(rehydratedTill),
          });
          window.history.replaceState(null, "", restoreTo);
        }

        trace("BootDiag", "tenantReady=true", { elapsedMs: Math.round(performance.now() - bootStart) });
        setTenantReady(true);
      } catch (err) {
        console.error("[App] Tenant init failed:", err);
        trace("BootDiag", "tenantReady=true (after error)", { elapsedMs: Math.round(performance.now() - bootStart), error: err?.message });
        setTenantReady(true);
      }
    };
    initTenant();
  }, []);

  useEffect(() => {
    if (SentryEnabled) {
      Sentry.init({
        environment: environment,
        dsn: SentryDSN,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: 1.0,
        ignoreErrors: [
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          "ResizeObserver loop completed with undelivered notifications.",
        ],
      });
    }
  }, []);

  const wsUrl = process.env.REACT_APP_WS_URL;

  // Wait for tenant to load before rendering the app.
  // Uses the same antd <Spin size="large" /> as the index.html boot splash so the
  // loading visual stays consistent from hard-reload through tenant/store rehydration.
  if (!tenantReady) {
    return (
      <div className="aspectRatio" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <SingleInstanceGuard>
      <AppShell wsUrl={wsUrl} />
    </SingleInstanceGuard>
  );
};

const AppShell = ({ wsUrl }) => {
  const tillData = useAuthStore((s) => s.tillData);
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId || null;
  const tillId = tillData?.tillAccess?.cwrTill?.till || null;
  const tillLayout = parseTillLayout(tillData);

  return (
    <div className="aspectRatio" style={{ display: "flex", flexDirection: "column" }}>
      <CrashRecoveryProvider getSessionState={() => ({})} onRestore={() => {}}>
        <I18nextProvider i18n={i18n}>
          <EventBusProvider>
            <WebSocketProvider url={wsUrl} bunitId={bunitId} tillId={tillId} tillLayout={tillLayout} enabled={!!bunitId} debug={true}>
              <AppContent />
            </WebSocketProvider>
          </EventBusProvider>
        </I18nextProvider>
      </CrashRecoveryProvider>
      <SupportButton />
      <CustomerReviewHost />
      {/* BUG-2570 v2: print failures when no payment panel / table grid bar is on screen */}
      <PrintFailureBar placement="floating" />
    </div>
  );
};

const AppContent = () => {
  const { visibleOrders, lastMessage, removeVisibleOrder, removeVisibleOrderByExternalId } = useWebSocket();

  const handleAutoAccept = async (orderData, { isManualAccept } = {}) => {
    // Previously only printed on isManualAccept -- the 30s-timeout auto-accept
    // path (isManualAccept: false) relied entirely on a later order:update
    // WS round-trip from the backend to trigger print via handleOrderUpdate
    // below. If that message was delayed, dropped, or the WS reconnected in
    // between, the order was acknowledged but never printed, with no retry.
    // getSalesHistoryDetails also runs for the same order via handleOrderUpdate
    // below when that WS round-trip does arrive, so both accept types can race
    // this call against it. BUG-1971: this used to genuinely double-print when
    // both landed close together, because the dedup guard ran after each call's
    // own network fetch. It now claims the guard atomically before the fetch
    // (see claimPrintOnce in printGuard.js), so only whichever call gets there
    // first actually prints -- see tests/unit/bugs/bug1971_aggregator_no_kot_duplicate_print.test.js.
    //
    // BUG-2322: this used to claim/fetch on orderData.data.orderId (the
    // internal s_order_id), while handleOrderUpdate below claims/fetches on
    // the aggregator's external channel id -- confirmed from live cw-socket
    // logs that order:new carries both orderId (internal UUID) and externalId
    // (the same merchant-reference value order:update's
    // additional_info.external_channel.order_id carries), but order:update
    // never carries the internal UUID at all. Because the two call sites
    // claimed under different keys, claimPrintOnce (BUG-1971) never actually
    // saw them as the same order, so the guard it added didn't stop this
    // race -- both could still print. Switched to externalId here to match
    // handleOrderUpdate; the backend's salesHistoryDetails resolver accepts
    // either s_order_id or external_id (SaleshistoryImpl.java), so this is a
    // safe, non-breaking swap of which id we send.
    if (orderData?.data?.externalId) {
      const result = await getSalesHistoryDetails(orderData?.data?.externalId, "AK", true);
      console.debug("[ORDER-ACCEPT-DIAG] getSalesHistoryDetails (auto-print) returned", { externalId: orderData?.data?.externalId, isManualAccept, hadResult: !!result });
    } else {
      console.debug("[ORDER-ACCEPT-DIAG] handleAutoAccept skipped print call - no externalId", { isManualAccept });
    }
    removeVisibleOrder(orderData?.data?.orderId);
  };

  const handleOrderUpdate = async (data) => {
    try {
      console.debug("[ORDER-ACCEPT-DIAG] handleOrderUpdate received order:update", { new_state: data?.new_state, order_id: data?.order_id, external_channel_order_id: data?.additional_info?.external_channel?.order_id });
      if (data?.new_state === "Acknowledged") {
        const externalOrderId = data?.additional_info?.external_channel?.order_id;
        console.debug("Handling order:update for external order ID:", externalOrderId);
        const orderDetails = await getSalesHistoryDetails(externalOrderId, "AK", true, data?.order_id);

        if (!orderDetails) {
          console.error(`No order details for external order ID: ${externalOrderId}`);

          return;
        }

        removeVisibleOrderByExternalId(externalOrderId);
      } else {
        console.debug("[ORDER-ACCEPT-DIAG] order:update ignored — new_state is not 'Acknowledged'", { new_state: data?.new_state });
      }
    } catch (error) {
      console.error("Error handling websocket order:update:", error);
    }
  };

  useEffect(() => {
    if (!lastMessage) return;

    const eventType = lastMessage?.type || lastMessage?.event;

    if (eventType === "order:update") {
      handleOrderUpdate(lastMessage?.data);
    }
  }, [lastMessage]);

  const handleReject = (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  const handleOnClose = (orderData) => {
    removeVisibleOrder(orderData?.data?.orderId);
  };

  return (
    <Router>
      <ErrorBoundary>
        <NavigationBlocker />
        <TillModalHandler />
        {visibleOrders.map(
          (order, index) => (
            // window.location.pathname !== "/aggregator" ? (
            <OrderNotification
              key={order.data.orderId}
              orderData={order}
              index={index}
              onAutoAccept={(options) => handleAutoAccept(order, options)}
              onReject={() => handleReject(order)}
              onClose={() => handleOnClose(order)}
            />
          )
          // ) : null
        )}
        <Switch>
          {/* Public routes */}
          <Route exact path="/sign-in" component={SignIn} />
          <Route exact path="/health" component={HealthMonitor} />
          <Route exact path="/till" component={Till} />
          <Route exact path="/login" component={Login} />

          {/* Private POS routes */}
          <PrivateRoute exact path="/pos" component={PointOfSale} />
          <PrivateRoute exact path="/dualscreen" component={DualScreenPOS} />
          <PrivateRoute exact path="/sync" component={Sync} />
          <PrivateRoute exact path="/dashboard2" component={Dashboard2} errorBoundary="Dashboard" />
          <PrivateRoute exact path="/production" component={ProductionOrder} errorBoundary="Production" />
          <PrivateRoute exact path="/production-rm-issue" component={ProductionRMIssue} errorBoundary="Production RM" />
          <PrivateRoute exact path="/purchaseOrder" component={PurchaseOrder} errorBoundary="Purchase Order" />
          <PrivateRoute exact path="/inventory/stock-indent" component={Inventory} errorBoundary="Inventory" />
          <PrivateRoute exact path="/aggregator" component={Aggregator} errorBoundary="Aggregator" />
          <PrivateRoute exact path="/estimate" component={Estimate} errorBoundary="Estimate" />
          <PrivateRoute exact path="/preorder" component={Preorder} errorBoundary="Preorder" />
          <PrivateRoute exact path="/restaurant/preorder" component={RestaurantPreorder} errorBoundary="Restaurant Preorder" />
          <PrivateRoute exact path="/open-till" component={OpenTill} />
          <PrivateRoute exact path="/cash-to-keep" component={CashToKeep} />
          <PrivateRoute exact path="/open-till-component" component={OpenTillComponent} />
          <PrivateRoute exact path="/close-till" component={CloseTill} />
          <PrivateRoute exact path="/close-till-all" component={CloseTillAll} />
          <PrivateRoute exact path="/table-management" component={TableManagement} errorBoundary="Table Management" />
          <PrivateRoute exact path="/call-center" component={CallCenter} errorBoundary="Call Center" />

          {/* Catch-all: redirect based on auth state */}
          <Route component={AuthRedirect} />
        </Switch>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
